import React, { useState, useEffect } from 'react';
import { reportsAPI, locationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  BarChart3, 
  Package, 
  AlertTriangle, 
  Clock,
  Download,
  FileText,
  Laptop,
  Smartphone,
  DollarSign
} from 'lucide-react';
import { formatDate, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { itemsAPI } from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';

const downloadPdf = async (apiCall, filename) => {
  try {
    const response = await apiCall();
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  } catch (error) {
    toast.error('Failed to download report');
    console.error(error);
  }
};

export default function Reports() {
  const [inventoryReport, setInventoryReport] = useState([]);
  const [issuesReport, setIssuesReport] = useState([]);
  const [agingReport, setAgingReport] = useState([]);
  const [itAssets, setItAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadInventoryReport();
  }, [selectedLocation]);

  const loadData = async () => {
    try {
      const [locRes, invRes, issuesRes, agingRes, itemsRes] = await Promise.all([
        locationsAPI.list(),
        reportsAPI.inventorySummary(),
        reportsAPI.employeeIssues(),
        reportsAPI.requestAging(),
        itemsAPI.listAll({ category: 'it_assets' })
      ]);
      setLocations(locRes.data);
      setInventoryReport(invRes.data);
      setIssuesReport(issuesRes.data);
      setAgingReport(agingRes.data);
      setItAssets(itemsRes.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryReport = async () => {
    try {
      const params = selectedLocation !== 'all' ? { location_id: selectedLocation } : {};
      const response = await reportsAPI.inventorySummary(params);
      setInventoryReport(response.data);
    } catch (error) {
      console.error('Failed to load inventory report:', error);
    }
  };

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      switch (type) {
        case 'inventory':
          await downloadPdf(() => reportsAPI.downloadInventoryPdf(selectedLocation !== 'all' ? { location_id: selectedLocation } : {}), 'inventory_summary.pdf');
          break;
        case 'issues':
          await downloadPdf(() => reportsAPI.downloadEmployeeIssuesPdf(), 'employee_issues.pdf');
          break;
        case 'pos':
          await downloadPdf(() => reportsAPI.downloadPurchaseOrdersPdf(), 'purchase_orders.pdf');
          break;
        case 'aging':
          await downloadPdf(() => reportsAPI.downloadRequestAgingPdf(), 'request_aging.pdf');
          break;
        case 'it-assets':
          await downloadPdf(() => reportsAPI.downloadItAssetsPdf(), 'it_assets_report.pdf');
          break;
        case 'low-stock':
          await downloadPdf(() => reportsAPI.downloadLowStockPdf(selectedLocation !== 'all' ? { location_id: selectedLocation } : {}), 'low_stock_alert.pdf');
          break;
        default:
          break;
      }
    } finally {
      setDownloading('');
    }
  };

  const lowStockItems = inventoryReport.filter(i => i.is_low_stock);
  const overdueRequests = agingReport.filter(r => r.is_overdue);

  const ASSET_TYPE_LABELS = { laptop: 'Laptop', phone: 'Mobile Phone', tablet: 'Tablet', radio: 'Two-Way Radio', camera: 'Body Camera', other: 'Other' };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="text-slate-500 mt-1">View and download inventory and operational reports</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleDownload('inventory')}
            disabled={downloading === 'inventory'}
            data-testid="download-all-inventory-pdf"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading === 'inventory' ? 'Downloading...' : 'All Reports PDF'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inventoryReport.length}</p>
                <p className="text-sm text-slate-500">Inventory SKUs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{lowStockItems.length}</p>
                <p className="text-sm text-slate-500">Low Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{overdueRequests.length}</p>
                <p className="text-sm text-slate-500">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{issuesReport.length}</p>
                <p className="text-sm text-slate-500">Active Issues</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Laptop className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{itAssets.length}</p>
                <p className="text-sm text-slate-500">IT Assets</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reports Tabs */}
      <Tabs defaultValue="inventory" className="space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-max sm:w-auto">
            <TabsTrigger value="inventory">Inventory</TabsTrigger>
            <TabsTrigger value="inv-value">Inventory Value</TabsTrigger>
            <TabsTrigger value="low-stock">Low Stock</TabsTrigger>
            <TabsTrigger value="issues">Issues</TabsTrigger>
            <TabsTrigger value="aging">Aging</TabsTrigger>
            <TabsTrigger value="pos">POs</TabsTrigger>
            <TabsTrigger value="it-assets">IT Assets</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="inventory">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Inventory Summary</CardTitle>
                  <CardDescription>Stock levels across all locations</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger className="w-48" data-testid="location-filter">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={() => handleDownload('inventory')} disabled={!!downloading} data-testid="download-inventory-pdf">
                    <FileText className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-12 skeleton-pulse rounded"></div>
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">On Hand</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                      <TableHead className="text-right">Min Stock</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventoryReport.map((inv, index) => (
                      <TableRow key={`${inv.variant_sku || 'na'}-${index}`}>
                        <TableCell className="font-medium">{inv.item_name}</TableCell>
                        <TableCell className="font-mono">{inv.variant_sku}</TableCell>
                        <TableCell>{inv.category}</TableCell>
                        <TableCell className="text-right">{inv.quantity}</TableCell>
                        <TableCell className="text-right">{inv.available}</TableCell>
                        <TableCell className="text-right">{inv.min_stock}</TableCell>
                        <TableCell>
                          {inv.is_low_stock ? (
                            <Badge variant="destructive" className="bg-amber-100 text-amber-700">
                              Low Stock
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-600">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {inventoryReport.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No inventory records</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="low-stock">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Low Stock Alert</CardTitle>
                  <CardDescription>Items below minimum stock threshold</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleDownload('low-stock')} disabled={!!downloading} data-testid="download-low-stock-pdf">
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {lowStockItems.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Minimum</TableHead>
                      <TableHead className="text-right">Shortage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lowStockItems.map((inv, index) => (
                      <TableRow key={`${inv.variant_sku || 'na'}-${index}`}>
                        <TableCell className="font-medium">{inv.item_name}</TableCell>
                        <TableCell className="font-mono">{inv.variant_sku}</TableCell>
                        <TableCell className="text-right text-red-600 font-medium">{inv.quantity}</TableCell>
                        <TableCell className="text-right">{inv.min_stock}</TableCell>
                        <TableCell className="text-right text-red-600">
                          {inv.min_stock - inv.quantity}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No low stock items</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="issues">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Employee Issues</CardTitle>
                  <CardDescription>Items currently assigned to employees</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleDownload('issues')} disabled={!!downloading} data-testid="download-issues-pdf">
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {issuesReport.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Condition</TableHead>
                      <TableHead>Issued Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {issuesReport.map((issue, index) => (
                      <TableRow key={`${issue.checkout_id || issue.id || 'na'}-${index}`}>
                        <TableCell className="font-medium">{issue.employee_name || 'Unknown employee'}</TableCell>
                        <TableCell>{issue.item_name}</TableCell>
                        <TableCell className="font-mono">{issue.variant_sku}</TableCell>
                        <TableCell className="text-right">{issue.quantity}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{issue.condition}</Badge>
                        </TableCell>
                        <TableCell>{formatDate(issue.issued_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No active issues</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aging">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Requisition Aging</CardTitle>
                  <CardDescription>Open requests sorted by age</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleDownload('aging')} disabled={!!downloading} data-testid="download-aging-pdf">
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {agingReport.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request #</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Needed By</TableHead>
                      <TableHead className="text-right">Age (Days)</TableHead>
                      <TableHead>Alert</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agingReport.map((req, index) => (
                      <TableRow key={`${req.id || req.request_number || 'na'}-${index}`}>
                        <TableCell className="font-mono">{req.request_number}</TableCell>
                        <TableCell className="font-medium">{req.employee_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{req.status}</Badge>
                        </TableCell>
                        <TableCell>{req.needed_by ? formatDate(req.needed_by) : '-'}</TableCell>
                        <TableCell className="text-right">{req.age_days}</TableCell>
                        <TableCell>
                          {req.is_overdue && (
                            <Badge variant="destructive">Overdue</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No open requests</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pos">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Purchase Order History</CardTitle>
                  <CardDescription>All purchase orders and their status</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleDownload('pos')} disabled={!!downloading} data-testid="download-pos-pdf">
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <POTable />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="it-assets">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>IT Asset Report</CardTitle>
                  <CardDescription>All laptops, phones, and IT equipment</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleDownload('it-assets')} disabled={!!downloading} data-testid="download-it-assets-pdf">
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {itAssets.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Make</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Serial #</TableHead>
                      <TableHead>Asset Tag</TableHead>
                      <TableHead>Phone / IMEI</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itAssets.map((asset) => (
                      <TableRow key={asset.id}>
                        <TableCell className="font-medium">{asset.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{asset.make || '-'}</TableCell>
                        <TableCell>{asset.model || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{asset.serial_number || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{asset.asset_tag || '-'}</TableCell>
                        <TableCell className="text-sm">
                          {asset.phone_number ? <span>{asset.phone_number}</span> : null}
                          {asset.imei ? <span className="block font-mono text-xs text-slate-500">IMEI: {asset.imei}</span> : null}
                          {!asset.phone_number && !asset.imei ? '-' : null}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(asset.unit_cost)}</TableCell>
                        <TableCell>
                          <Badge variant={asset.is_active ? 'default' : 'secondary'}>
                            {asset.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Laptop className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No IT assets found</p>
                  <p className="text-sm">Add IT assets in the Asset Catalog</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INVENTORY VALUE REPORT */}
        <TabsContent value="inv-value">
          <InventoryValueReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function POTable() {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPOs = async () => {
      try {
        const { purchaseOrdersAPI } = await import('../lib/api');
        const res = await purchaseOrdersAPI.list();
        setPos(res.data);
      } catch (e) {
        console.error('Failed to load POs:', e);
      } finally {
        setLoading(false);
      }
    };
    loadPOs();
  }, []);

  if (loading) return <div className="h-24 skeleton-pulse rounded"></div>;

  if (pos.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <p>No purchase orders found</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>PO #</TableHead>
          <TableHead>Vendor</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Items</TableHead>
          <TableHead className="text-right">Total</TableHead>
          <TableHead>Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {pos.map((po) => (
          <TableRow key={po.id}>
            <TableCell className="font-mono">{po.po_number}</TableCell>
            <TableCell className="font-medium">{po.vendor_name}</TableCell>
            <TableCell>{po.location_name}</TableCell>
            <TableCell>
              <Badge variant="outline" className="capitalize">{po.status}</Badge>
            </TableCell>
            <TableCell>{po.lines?.length || 0}</TableCell>
            <TableCell className="text-right">{formatCurrency(po.total_amount)}</TableCell>
            <TableCell>{formatDate(po.created_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}


function InventoryValueReport() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (startDate > endDate) {
      toast.error('Start date must be before end date');
      return;
    }
    setGenerating(true);
    try {
      const res = await api.get(`/reports/inventory-value/pdf?start_date=${startDate}&end_date=${endDate}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_value_${startDate}_to_${endDate}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally { setGenerating(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" /> Monthly Inventory Value Report</CardTitle>
        <CardDescription>Generate a PDF report of uniform and equipment inventory values by location. IT assets are excluded.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-slate-50 rounded-lg p-4 space-y-1 text-sm text-slate-600">
          <p>This report includes <strong>Uniforms</strong> and <strong>Equipment</strong> inventory only.</p>
          <p>IT Assets are excluded. Each location gets its own section with line-item detail.</p>
          <p>A summary page at the front shows each location's total value and the grand total.</p>
        </div>
        <div className="flex items-end gap-4 flex-wrap">
          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-[180px]" data-testid="inv-report-start" />
          </div>
          <div className="space-y-2">
            <Label>End Date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-[180px]" data-testid="inv-report-end" />
          </div>
          <Button onClick={handleGenerate} disabled={generating || !startDate || !endDate} data-testid="generate-inv-report-btn">
            <FileText className="w-4 h-4 mr-2" /> {generating ? 'Generating...' : 'Generate PDF Report'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
